import { generateFinalUrl } from "../../../shared/helpers";

export const DENSITY_COLORS = ["#1a3faa", "#e63946", "#f4a261", "#4a9eb7", "#2d9a5e"];

const _htmlCache = {};
const _htmlEl = document.createElement("div");

export const msgtxt = (html) =>
  html?.replace(/([&][^&; ]+[;])/g, (entity) => {
    if (!_htmlCache[entity]) {
      _htmlEl.innerHTML = entity;
      _htmlCache[entity] = _htmlEl.childNodes[0]?.nodeValue ?? "";
    }
    return _htmlCache[entity];
  });

export const getTopSkills = (skills = []) => {
  if (!Array.isArray(skills) || skills.length === 0) return [];
  return [...skills]
    .sort((a, b) => b.SkillPercent - a.SkillPercent)
    .slice(0, 3)
    .map((skill) => ({ id: skill.Skills.SkillName, name: skill.Skills.SkillName }));
};

const getArrayObject = (name = "", id = "") => [{ name, id }];

export const getAsRequiredForQuery = ({ regions, countries, cities, skills = [] }) => {
  const accumulatedFilters = {};

  const addFilterToUrl = (key, value) => {
    if (value != null) {
      const str = typeof value === "object" ? JSON.stringify(value) : String(value);
      accumulatedFilters[key] = str.replace(/&/g, "-*");
    }
  };

  const checkRadioSkill = getTopSkills(skills);

  const values = [
    { key: "Region",            value: regions   ? getArrayObject(regions.RegionName, regions.RegionId)       : undefined },
    { key: "regionId",          value: regions   ? [regions.RegionId]                                         : undefined },
    { key: "Country",           value: countries ? getArrayObject(countries.CountryName, countries.CountryId) : undefined },
    { key: "countryId",         value: countries ? [countries.CountryId]                                      : undefined },
    { key: "City",              value: cities    ? getArrayObject(cities.Location, cities.CityId)              : undefined },
    { key: "cityId",            value: cities    ? [cities.CityId]                                            : undefined },
    { key: "Skill",             value: checkRadioSkill },
    { key: "skillsId",          value: checkRadioSkill.map((el) => el.name) },
    { key: "skillsPercentages", value: [...skills].sort((a, b) => b.SkillPercent - a.SkillPercent).slice(0, 3).map((el) => el.SkillPercent) },
    { key: "searchType",        value: "talAutoSuggested" },
    { key: "isTalentSuggestion",value: true },
  ];

  values.forEach(({ key, value }) => addFilterToUrl(key, value));
  const path = generateFinalUrl("/internal", accumulatedFilters);
  localStorage.setItem("talentSearchQuery", path);
  return path;
};

export const getExperienceLevelsArray = (jobdetails) => {
  const arr = jobdetails[0]?.ExperienceLevels;
  if (Array.isArray(arr)) {
    return [...arr]
      .sort((a, b) => a.ExperienceLevelId - b.ExperienceLevelId)
      .map((i) => i.ExperienceLevelName);
  }
  return arr?.ExperienceLevelName ? [arr.ExperienceLevelName] : [];
};

export const buildFindMatchesParams = (jobdetails) => {
  const job = jobdetails[0];

  const workAuthorizationMapping = {
    "US Resident/Citizen": 5,
    Citizen: 5,
    EAD: 9,
    "H1-B": 8,
  };

  let countryIds = [], countryObj = [], regionObj = [], regionIds = [], cityObj = [], cityIds = [];
  let expLevels = [], Industry = [], industryId = [];
  let workAuth = [], workAuthIds = [];

  if (job.Location) {
    const loc = job.Location;
    countryIds = [parseInt(loc.CountryId)];
    countryObj = [{ name: loc.CountryName, id: loc.CountryId }];
    regionObj  = [{ name: loc.RegionName,  id: loc.RegionId  }];
    regionIds  = [parseInt(loc.RegionId)];
    cityObj    = [{ name: loc.Location,    ids: loc.CityId   }];
    cityIds    = [parseInt(loc.CityId)];
  }

  if (job.ExperienceLevels?.length) {
    expLevels = JSON.stringify(job.ExperienceLevels.map((item) => item.ExperienceLevelName));
  }

  if (job.Industry?.length) {
    Industry   = job.Industry.map((item) => ({ name: item.IndustryName, id: item.IndustryName }));
    industryId = job.Industry.map((item) => item.IndustryName);
  }

  if (job.WorkAuthorization) {
    workAuth     = job.WorkAuthorization.map((item) => ({
      name: item === "Citizen" ? "US Resident/Citizen" : item,
      id: workAuthorizationMapping[item],
    }));
    workAuthIds = workAuth.map((item) => item.id);
  }

  const toJson = (arr) => (arr.length > 0 ? JSON.stringify(arr) : "");

  const queryParams = new URLSearchParams({
    skillListing_type: job.JobOrderTypes?.JobOrderTypeName,
    experience:        expLevels,
    Industry:          toJson(Industry),
    industryId:        toJson(industryId),
    Country:           toJson(countryObj),
    countryId:         toJson(countryIds),
    Region:            toJson(regionObj),
    regionId:          toJson(regionIds),
    City:              toJson(cityObj),
    cityId:            toJson(cityIds),
    VisaName:          toJson(workAuth),
    VisaStatusId:      toJson(workAuthIds),
    isPrivateTalent:   job?.isPrivate,
    searchType:        "Match",
  });

  return {
    pathname: `/findmatches/${encodeURIComponent(job?.JobOrderId)}`,
    state: { Id: job?.JobOrderId, Title: job.skills?.SkillName, jobdetails },
    search: queryParams.toString(),
  };
};

export const buildPiePlot = (jobdetails) => {
  if (!jobdetails?.length || !jobdetails[0]?.JobOrders_Skills?.length) return null;

  const rawSkills = jobdetails[0].JobOrders_Skills;

  const dataArrayTemp = rawSkills.map((item) => ({
    ...item,
    Skills: !item.Skills || item.Skills.SkillName === null
      ? { SkillName: "Others", SkillPercent: item.SkillPercent }
      : item.Skills,
  }));

  const nullItems = dataArrayTemp.filter(
    (item) => item.SkillPercent === null || item.Skills?.SkillPercent === null
  );
  const sum = nullItems.reduce((acc, item) => acc + item.SkillPercent, 0);

  if (sum !== 100 && nullItems.length > 0) {
    const proportion = (100 - sum) / nullItems.length;
    nullItems.forEach((item) => { item.SkillPercent += proportion; });
  }

  const dataArray = dataArrayTemp.map((item, index) => ({
    type:  item.Skills?.SkillName ?? item.SkillName,
    value: rawSkills[index].SkillPercent,
  }));

  return {
    appendPadding: 5,
    data:          dataArray,
    angleField:    "value",
    colorField:    "type",
    radius:        1.0,
    innerRadius:   0.6,
    height:        130,
    width:         130,
    legend:        false,
    color:         DENSITY_COLORS,
    label:         false,
    statistic: {
      title: {
        offsetY: -4,
        style:   { fontSize: "20px", fontWeight: "700", color: "#1e1e1e" },
        content: String(rawSkills.length),
      },
      content: {
        offsetY: 4,
        style:   { fontSize: "12px", color: "#7e7e7e" },
        content: "Skills",
      },
    },
    interactions: [{ type: "element-active" }],
  };
};
